import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft } from "lucide-react";
import { Section, SectionHeader } from "@/components/site/Section";
import { BehaviorQuestionnaire } from "@/components/site/BehaviorQuestionnaire";
import { RaterLinkManager } from "@/components/site/RaterLinkManager";
import {
  ANON_THRESHOLD,
  relationshipLabels,
  type Answers,
  type Relationship,
} from "@/content/review";
import {
  addRater,
  cycleDetail,
  removeRater,
  saveSelfRating,
  type RaterRow,
} from "@/utils/review.functions";

export const Route = createFileRoute("/_authenticated/my-review/$cycleId/")({
  head: () => ({
    meta: [
      { title: "Run your 360 check | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Rate yourself, add raters, and follow how many people in each group have answered.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Run your 360 check" },
      {
        property: "og:description",
        content: "Rate yourself and invite the people who work with you.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: CyclePage,
});

interface Detail {
  id: string;
  leaderName: string | null;
  status: string;
  unlocked: boolean;
  selfComplete: boolean;
  raters: RaterRow[];
}

function CyclePage() {
  const { cycleId } = useParams({ from: "/_authenticated/my-review/$cycleId/" });
  const load = useServerFn(cycleDetail);
  const saveSelf = useServerFn(saveSelfRating);
  const add = useServerFn(addRater);
  const drop = useServerFn(removeRater);

  const [detail, setDetail] = useState<Detail | null>(null);
  const [links, setLinks] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [rating, setRating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(() => {
    load({ data: { cycleId } })
      .then(setDetail)
      .catch(() => setMessage("We couldn't load this check."));
  }, [load, cycleId]);

  useEffect(refresh, [refresh]);

  if (!detail) {
    return (
      <Section>
        <p className="text-sm text-muted-foreground">{message ?? "Loading this check."}</p>
      </Section>
    );
  }

  if (rating) {
    return (
      <div className="min-h-[calc(100vh-5rem)] bg-lavender py-16">
        <BehaviorQuestionnaire
          relationship="self"
          submitting={busy}
          submitLabel="Save my self-rating"
          onSubmit={(answers: Answers) => {
            setBusy(true);
            saveSelf({ data: { cycleId, answers } })
              .then(() => {
                setRating(false);
                refresh();
              })
              .catch(() => setMessage("We couldn't save your answers."))
              .finally(() => setBusy(false));
          }}
        />
      </div>
    );
  }

  const counts = (["direct_report", "peer", "manager"] as Relationship[]).map((rel) => {
    const inGroup = detail.raters.filter((r) => r.relationship === rel);
    const submitted = inGroup.filter((r) => r.submitted).length;
    const needed =
      rel === "manager" ? Math.max(1 - submitted, 0) : Math.max(ANON_THRESHOLD - submitted, 0);
    return { rel, submitted, invited: inGroup.length, needed };
  });

  return (
    <Section>
      <Link
        to="/my-review/"
        className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-primary"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to your checks
      </Link>

      <SectionHeader
        eyebrow="360 Leader Behavior Check"
        title={detail.leaderName || "Your check"}
        intro="Your own rating comes first. Then send a private link to each person who will rate."
        titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
      />

      {message && <p className="mb-6 text-sm text-primary">{message}</p>}

      <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        <div className="rounded-2xl border border-border/60 bg-background p-7">
          <h3 className="font-display text-xl font-semibold text-ink">Your self-rating</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {detail.selfComplete
              ? "Done. You can redo it if something has changed."
              : "Sixteen questions, about five minutes."}
          </p>
          <button
            type="button"
            onClick={() => setRating(true)}
            className="mt-5 inline-flex items-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand"
          >
            {detail.selfComplete ? "Redo my self-rating" : "Rate myself"}
          </button>

          <h3 className="mt-9 font-display text-xl font-semibold text-ink">Who has answered</h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            {counts.map(({ rel, submitted, invited, needed }) => (
              <li key={rel}>
                <span className="font-semibold text-ink">{relationshipLabels[rel]}</span>{" "}
                {submitted} of {invited} answered
                {needed > 0 ? ` · waiting on ${needed} more` : " · ready"}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
            Counts only. You never see who answered or what any one person said.
          </p>

          <Link
            to="/my-review/$cycleId/results/"
            params={{ cycleId }}
            className="mt-6 inline-flex items-center rounded-[6px] border border-primary px-6 py-3 text-sm font-semibold text-primary"
          >
            View results
          </Link>
        </div>

        <RaterLinkManager
          raters={detail.raters}
          links={links}
          busy={busy}
          onAdd={(relationship, email) => {
            setBusy(true);
            setMessage(null);
            add({ data: { cycleId, relationship, email } })
              .then((result) => {
                setLinks((prev) => ({ ...prev, [result.raterId]: result.link }));
                setMessage(
                  result.emailSent
                    ? "Added, and the link is on its way to their inbox."
                    : "Added. Copy their link below; it is only shown once.",
                );
                refresh();
              })
              .catch((error: Error) => setMessage(error.message || "We couldn't add that person."))
              .finally(() => setBusy(false));
          }}
          onRemove={(raterId) => {
            setBusy(true);
            drop({ data: { cycleId, raterId } })
              .then(refresh)
              .catch(() => setMessage("We couldn't remove that person."))
              .finally(() => setBusy(false));
          }}
        />
      </div>
    </Section>
  );
}
